/**
 * HomePageForSupplier Component
 * 협력사 전용 홈 페이지 (배민 SLA 관리)
 */

import React, { useRef, useEffect, useCallback, useState, TouchEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../core/components/BottomNavigation';
import BalanceDisplay from './BalanceDisplay';
import CustomSelect, { SelectOption } from '../../../core/components/CustomSelect';
import { useHomePageForSupplier } from '../hooks/useHomePageForSupplier';
import { useDeferredLoading } from '../../../core/hooks/useDeferredLoading';
import { AutoRefreshInterval, RiderData, SupplierTabType } from '../types/homePageForSupplier';
import NoticePopupModal from '../../system/notice/components/NoticePopupModal';
import { getMobileUnreadPopupNotices, markMobileNoticeAsRead } from '../../system/notice/api/noticeApi';
import { NoticeResult } from '../../system/notice/types/notice.types';
import { useMaintenanceContext } from '../../system/maintenance/contexts/MaintenanceContext';
import MaintenanceScreen from '../../system/maintenance/components/MaintenanceScreen';
import styles from '../styles/HomePageForSupplier.module.css';

const HomePageForSupplier: React.FC = () => {
    const navigate = useNavigate();
    const { activeMaintenance, isMaintenance, loading: maintenanceLoading } = useMaintenanceContext();
    const {
        // 사용자 정보
        supplierInfo,

        // 탭 상태
        activeTab,
        handleTabChange,

        // 자동 갱신 상태
        autoRefreshInterval,
        showAutoRefreshSelect,
        lastUpdate,
        handleAutoRefreshToggle,
        handleAutoRefreshChange,

        // 옵션 목록
        weekOptions,
        dateOptions,
        todayDayOfWeek,
        gradeOptions,
        setCountOptions,
        autoRefreshOptions,

        // 정보 설정 탭
        dateRange,
        setCount,
        grade,
        handleDateRangeChange,
        handleSetCountChange,
        handleGradeChange,
        handleSaveSettings,

        // 주간 현황 탭
        weeklyStats,
        adjustedSlotStats,
        dailyData,
        fetchWeeklyStatus,
        weeklyStatusOptions,
        selectedWeeklyOption,
        handleWeeklyOptionChange,

        // 금일 달성현황 탭
        selectedDate,
        dailyStats,
        timeSlots,
        handleSelectedDateChange,
        fetchDailyStatus,

        // 라이더현황 탭 (당일)
        riderStats,
        riders,
        fetchRiderStatus,

        // 주간 라이더현황
        weeklyRiderStats,
        weeklyRiders,
        fetchWeeklyRiderStatus,

        // 공통 상태
        isLoading: rawIsLoading,
        error,
    } = useHomePageForSupplier();

    // 지연 로딩 적용 (200ms 이상 로딩 시에만 로딩 UI 표시)
    const isLoading = useDeferredLoading(rawIsLoading, 200);

    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
        fetchDailyStatus();
        fetchRiderStatus();
        fetchWeeklyStatus();
        fetchWeeklyRiderStatus();
    }, []);

    // 라이더 모달 상태
    const [selectedRider, setSelectedRider] = useState<RiderData | null>(null);
    const [isRiderModalOpen, setIsRiderModalOpen] = useState(false);

    // 공지사항 팝업 상태
    const [popupNotices, setPopupNotices] = useState<NoticeResult[]>([]);
    const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

    // 점검 화면 확인 여부 (세션당 한 번만 표시)
    const [maintenanceAcknowledged, setMaintenanceAcknowledged] = useState(() => {
        const acknowledged = sessionStorage.getItem('maintenance_acknowledged');
        const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
        // 점검 ID가 변경되었으면 확인 상태 초기화
        if (activeMaintenance && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
            sessionStorage.removeItem('maintenance_acknowledged');
            return false;
        }
        return acknowledged === 'true';
    });

    // 점검 상태가 변경되면 확인 상태 초기화
    useEffect(() => {
        if (!isMaintenance) {
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            setMaintenanceAcknowledged(false);
        } else if (activeMaintenance) {
            // 점검 ID가 변경되었는지 확인
            const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
            if (acknowledgedSeqNo && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
                sessionStorage.removeItem('maintenance_acknowledged');
                sessionStorage.removeItem('maintenance_acknowledged_seqno');
                setMaintenanceAcknowledged(false);
            }
        }
    }, [isMaintenance, activeMaintenance]);

    // 오늘 하루 보지 않기 처리된 공지 ID 가져오기
    const getHiddenNoticeIds = (): number[] => {
        const today = new Date().toISOString().split('T')[0];
        const stored = localStorage.getItem('hiddenNotices');
        if (!stored) return [];
        try {
            const data = JSON.parse(stored);
            if (data.date === today) {
                return data.ids || [];
            }
            return [];
        } catch {
            return [];
        }
    };

    // 오늘 하루 보지 않기 저장
    const hideNoticeForToday = (noticeId: number) => {
        const today = new Date().toISOString().split('T')[0];
        const hiddenIds = getHiddenNoticeIds();
        if (!hiddenIds.includes(noticeId)) {
            hiddenIds.push(noticeId);
        }
        localStorage.setItem('hiddenNotices', JSON.stringify({ date: today, ids: hiddenIds }));
    };

    // 공지사항 팝업 로드
    useEffect(() => {
        const fetchPopupNotices = async () => {
            try {
                const response = await getMobileUnreadPopupNotices();
                const hiddenIds = getHiddenNoticeIds();
                const filteredNotices = (response.notices || []).filter(
                    (notice) => !hiddenIds.includes(notice.noticeId)
                );
                setPopupNotices(filteredNotices);
            } catch (error) {
                console.error('팝업 공지 조회 실패:', error);
            }
        };
        fetchPopupNotices();
    }, []);

    // 공지사항 팝업 닫기
    const handleCloseNotice = async () => {
        const currentNotice = popupNotices[currentNoticeIndex];
        if (currentNotice) {
            try {
                await markMobileNoticeAsRead(currentNotice.noticeId);
            } catch (error) {
                console.error('공지 읽음 처리 실패:', error);
            }
        }
        if (currentNoticeIndex < popupNotices.length - 1) {
            setCurrentNoticeIndex(prev => prev + 1);
        } else {
            setPopupNotices([]);
            setCurrentNoticeIndex(0);
        }
    };

    // 오늘 하루 보지 않기
    const handleHideForToday = (noticeId: number) => {
        hideNoticeForToday(noticeId);
        handleCloseNotice();
    };

    // SelectOption 형식으로 변환
    const setCountSelectOptions: SelectOption[] = useMemo(() =>
        setCountOptions.map(opt => ({ value: opt, label: opt })),
        [setCountOptions]
    );

    const gradeSelectOptions: SelectOption[] = useMemo(() =>
        gradeOptions.map(opt => ({ value: opt, label: opt })),
        [gradeOptions]
    );

    const dateSelectOptions: SelectOption[] = useMemo(() =>
        dateOptions.map(opt => ({ value: opt.value, label: opt.label })),
        [dateOptions]
    );

    const weeklySelectOptions: SelectOption[] = useMemo(() =>
        weeklyStatusOptions.map(opt => ({ value: opt.value, label: opt.label })),
        [weeklyStatusOptions]
    );

    // 이름 툴팁 상태 (long press)
    const [nameTooltip, setNameTooltip] = useState<{ visible: boolean; name: string; x: number; y: number }>({
        visible: false,
        name: '',
        x: 0,
        y: 0,
    });
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 당일 라이더 페이지네이션 상태
    const [riderPage, setRiderPage] = useState(0);
    const RIDERS_PER_PAGE = 10;
    const totalRiderPages = Math.ceil(riders.length / RIDERS_PER_PAGE);
    const paginatedRiders = riders.slice(riderPage * RIDERS_PER_PAGE, (riderPage + 1) * RIDERS_PER_PAGE);

    // 주간 라이더 페이지네이션 상태
    const [weeklyRiderPage, setWeeklyRiderPage] = useState(0);
    const totalWeeklyRiderPages = Math.ceil(weeklyRiders.length / RIDERS_PER_PAGE);
    const paginatedWeeklyRiders = weeklyRiders.slice(weeklyRiderPage * RIDERS_PER_PAGE, (weeklyRiderPage + 1) * RIDERS_PER_PAGE);

    // 스와이프 관련 상태
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const riderTableRef = useRef<HTMLDivElement>(null);
    const weeklyRiderTableRef = useRef<HTMLDivElement>(null);

    const handleRiderRowClick = (rider: RiderData) => {
        setSelectedRider(rider);
        setIsRiderModalOpen(true);
    };

    const closeRiderModal = () => {
        setIsRiderModalOpen(false);
        setSelectedRider(null);
    };

    // 이름 Long Press 핸들러
    const handleNameTouchStart = (e: React.TouchEvent<HTMLSpanElement>, name: string) => {
        const touch = e.touches[0];
        longPressTimerRef.current = setTimeout(() => {
            setNameTooltip({
                visible: true,
                name,
                x: touch.clientX,
                y: touch.clientY - 50,
            });
        }, 1000); // 1초
    };

    const handleNameTouchEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const hideNameTooltip = () => {
        setNameTooltip(prev => ({ ...prev, visible: false }));
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        touchEndX.current = e.touches[0].clientX;
    }

    const handleTouchEnd = () => {
        const swipeThreshold = 50;
        const diff = touchStartX.current - touchEndX.current;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0 && riderPage < totalRiderPages - 1) {
                // 왼쪽으로 스와이프 -> 다음 페이지
                setRiderPage(prev => prev + 1);
            } else if (diff < 0 && riderPage > 0) {
                // 오른쪽으로 스와이프 -> 이전 페이지
                setRiderPage(prev => prev - 1);
            }
        }
    };

    // 주간 라이더 스와이프 핸들러
    const handleWeeklyTouchEnd = () => {
        const swipeThreshold = 50;
        const diff = touchStartX.current - touchEndX.current;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0 && weeklyRiderPage < totalWeeklyRiderPages - 1) {
                // 왼쪽으로 스와이프 -> 다음 페이지
                setWeeklyRiderPage(prev => prev + 1);
            } else if (diff < 0 && weeklyRiderPage > 0) {
                // 오른쪽으로 스와이프 -> 이전 페이지
                setWeeklyRiderPage(prev => prev - 1);
            }
        }
    };

    // 섹션 refs
    const containerRef = useRef<HTMLDivElement>(null);
    const settingsRef = useRef<HTMLDivElement>(null);
    const weeklyScoreRef = useRef<HTMLDivElement>(null);
    const dailyStatusRef = useRef<HTMLDivElement>(null);

    // 스크롤 중인지 확인 (탭 클릭으로 인한 스크롤 시 스크롤 감지 비활성화)
    const isScrollingByClick = useRef(false);

    // 탭 클릭 시 해당 섹션으로 스크롤
    const handleTabClick = useCallback((tab: SupplierTabType) => {
        const refMap: { [key in SupplierTabType]: React.RefObject<HTMLDivElement | null> } = {
            settings: settingsRef,
            weeklyScore: weeklyScoreRef,
            dailyStatus: dailyStatusRef,
            riderStatus: dailyStatusRef, // 라이더현황은 당일현황에 포함
        };

        const targetRef = refMap[tab];
        if (targetRef.current && containerRef.current) {
            isScrollingByClick.current = true;
            handleTabChange(tab);

            const headerHeight = 170; // 헤더 높이 보정 (탭 영역 포함)
            const targetPosition = targetRef.current.offsetTop - headerHeight;

            containerRef.current.scrollTo({
                top: targetPosition,
                behavior: 'smooth',
            });

            // 스크롤 애니메이션 완료 후 플래그 해제
            setTimeout(() => {
                isScrollingByClick.current = false;
            }, 500);
        }
    }, [handleTabChange]);

    // 스크롤 이벤트 핸들러 - 현재 보이는 섹션에 따라 탭 활성화
    const handleScroll = useCallback(() => {
        if (isScrollingByClick.current || !containerRef.current) return;

        const container = containerRef.current;
        const scrollTop = container.scrollTop;
        const headerHeight = 170;

        const sections: { ref: React.RefObject<HTMLDivElement | null>; tab: SupplierTabType }[] = [
            { ref: settingsRef, tab: 'settings' },
            { ref: dailyStatusRef, tab: 'dailyStatus' },
            { ref: weeklyScoreRef, tab: 'weeklyScore' },
        ];

        // 각 섹션의 위치를 확인하여 현재 보이는 섹션 찾기
        for (let i = sections.length - 1; i >= 0; i--) {
            const section = sections[i];
            if (section.ref.current) {
                const sectionTop = section.ref.current.offsetTop - headerHeight - 50;
                if (scrollTop >= sectionTop) {
                    if (activeTab !== section.tab) {
                        handleTabChange(section.tab);
                    }
                    break;
                }
            }
        }
    }, [activeTab, handleTabChange]);

    // 스크롤 이벤트 리스너 등록
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // 탭 메뉴 렌더링
    const renderTabs = () => (
        <div className={styles['supplier-tabs']}>
            <button
                className={`${styles['tab-btn']} ${activeTab === 'settings' ? styles['active'] : ''}`}
                onClick={() => handleTabClick('settings')}
            >
                정보 설정
            </button>
            <button
                className={`${styles['tab-btn']} ${activeTab === 'dailyStatus' ? styles['active'] : ''}`}
                onClick={() => handleTabClick('dailyStatus')}
            >
                당일 현황
            </button>
            <button
                className={`${styles['tab-btn']} ${activeTab === 'weeklyScore' ? styles['active'] : ''}`}
                onClick={() => handleTabClick('weeklyScore')}
            >
                주간 현황
            </button>
        </div>
    );

    // 정보 설정 섹션 렌더링
    const renderSettingsSection = () => (
        <div ref={settingsRef} className={styles['section']}>
            <h2 className={styles['section-title']}>정보 설정</h2>
            <div className={styles['section-content']}>
                <div className={styles['settings-row']}>
                    <div className={`${styles['settings-section']} ${styles['half']}`}>
                        <CustomSelect
                            label="세트수"
                            options={setCountSelectOptions}
                            value={setCount}
                            onChange={handleSetCountChange}
                            maxVisibleOptions={6}
                        />
                    </div>
                    <div className={`${styles['settings-section']} ${styles['half']}`}>
                        <CustomSelect
                            label="등급"
                            options={gradeSelectOptions}
                            value={grade}
                            onChange={handleGradeChange}
                            disabled={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // 당일 현황 섹션 렌더링
    const renderDailyStatusSection = () => (
        <div ref={dailyStatusRef} className={styles['section']}>
            <h2 className={styles['section-title']}>당일 현황</h2>
            <div className={styles['section-content']}>
                {/* 조회일자 선택 */}
                <div className={styles['date-selector-section']}>
                    <CustomSelect
                        label="조회일자"
                        options={dateSelectOptions}
                        value={selectedDate}
                        onChange={handleSelectedDateChange}
                    />
                </div>

                {/* 당일 통계 */}
                <div className={styles['daily-stats-cards']}>
                    <div className={styles['daily-stat-card']}>
                        <div className={styles['daily-stat-card-label']}>당일 총 완료 수</div>
                        <div className={styles['daily-stat-card-value']}>{dailyStats.completedCount}건</div>
                    </div>
                    <div className={styles['daily-stat-card']}>
                        <div className={styles['daily-stat-card-label']}>당일 총 거절 수</div>
                        <div className={styles['daily-stat-card-value']}>{dailyStats.rejectedCount}건</div>
                    </div>
                    <div className={styles['daily-stat-card']}>
                        <div className={styles['daily-stat-card-label']}>당일 수락률</div>
                        <div className={styles['daily-stat-card-value']}>{dailyStats.acceptanceRate}%</div>
                    </div>
                </div>

                {/* 시간대별 슬롯 */}
                <div className={styles['time-slots-list']}>
                    {timeSlots.map((slot) => {
                        const today = new Date();
                        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                        const selectedDateObj = new Date(selectedDate);
                        const todayObj = new Date(todayStr);
                        
                        let displayStatus = slot.status;
                        if (selectedDateObj < todayObj) {
                            displayStatus = 'completed'; // 과거: 진행완료
                        } else if (selectedDateObj > todayObj) {
                            displayStatus = 'waiting'; // 미래: 대기중
                        }
                        
                        return (
                            <div key={slot.id} className={styles['time-slot-card']}>
                                <div className={styles['time-slot-header']}>
                                    <div className={styles['time-slot-info']}>
                                        <span className={styles['time-slot-icon']}>{slot.icon}</span>
                                        <span className={styles['time-slot-name']}>{slot.name}</span>
                                        <span className={styles['time-slot-range']}>({slot.timeRange})</span>
                                    </div>
                                    <div className={`${styles['time-slot-status']} ${styles[displayStatus]}`}>
                                        {displayStatus === 'inProgress' ? '진행중' : displayStatus === 'waiting' ? '대기중' : '진행완료'}
                                    </div>
                                </div>
                                <div className={styles['time-slot-progress']}>
                                    <div
                                        className={styles['time-slot-progress-bar']}
                                        style={{ width: `${(slot.current / slot.target) * 100}%` }}
                                    />
                                </div>
                                <div className={styles['time-slot-footer']}>
                                    <span className={styles['time-slot-count']}>{slot.current}건 / {slot.target}건</span>
                                    <span className={styles['time-slot-rate']}>수행률: {slot.rate}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 당일 라이더 현황 */}
                <div className={styles['daily-rider-section']}>
                    <h3 className={styles['subsection-title']}>당일 라이더 현황</h3>
                    <div className={styles['rider-stats-cards']}>
                        <div className={`${styles['rider-stat-card']} ${styles['total']}`}>
                            <div className={styles['rider-stat-label']}>총 라이더</div>
                            <div className={styles['rider-stat-value']}>{riderStats.totalRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['active']}`}>
                            <div className={styles['rider-stat-label']}>운행중</div>
                            <div className={styles['rider-stat-value']}>{riderStats.activeRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['completed']}`}>
                            <div className={styles['rider-stat-label']}>운행종료</div>
                            <div className={styles['rider-stat-value']}>{riderStats.completedRiders}명</div>
                        </div>
                    </div>

                    {/* 당일 라이더 테이블 */}
                    <div
                        ref={riderTableRef}
                        className={styles['rider-table-container']}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {isLoading ? (
                            <div className={styles['rider-empty']}>
                                라이더 정보를 불러오는 중...
                            </div>
                        ) : error ? (
                            <div className={styles['rider-empty']} style={{ color: '#e74c3c' }}>
                                {error}
                            </div>
                        ) : riders.length === 0 ? (
                            <div className={styles['rider-empty']}>
                                등록된 라이더가 없습니다.
                            </div>
                        ) : (
                            <>
                                <div className={styles['rider-table-scroll']}>
                                    <table className={styles['rider-table']}>
                                        <thead>
                                        <tr>
                                            <th>이름</th>
                                            <th>운영상태</th>
                                            <th>전화번호</th>
                                            <th>수락건수</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {paginatedRiders.map((rider) => (
                                            <tr
                                                key={rider.id}
                                                onClick={() => handleRiderRowClick(rider)}
                                                className={styles['rider-table-row']}
                                            >
                                                <td>
                                                    <span
                                                        className={styles['rider-name']}
                                                        onTouchStart={(e) => handleNameTouchStart(e, rider.name)}
                                                        onTouchEnd={handleNameTouchEnd}
                                                        onTouchCancel={handleNameTouchEnd}
                                                    >
                                                        {rider.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles['rider-status-badge']} ${styles[rider.status]}`}>
                                                        {rider.status === 'active' ? '운행중' : '운행종료'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a
                                                        href={`tel:${rider.phoneNumber}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={styles['phone-link']}
                                                    >
                                                        {rider.phoneNumber}
                                                    </a>
                                                </td>
                                                <td>{rider.completedCount}건</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* 페이지 인디케이터 */}
                                {totalRiderPages > 1 && (
                                    <div className={styles['rider-pagination']}>
                                        <span className={styles['rider-pagination-text']}>
                                            {riderPage + 1} / {totalRiderPages}
                                        </span>
                                        <div className={styles['rider-pagination-dots']}>
                                            {Array.from({ length: totalRiderPages }, (_, i) => (
                                                <span
                                                    key={i}
                                                    className={`${styles['rider-pagination-dot']} ${i === riderPage ? styles['active'] : ''}`}
                                                />
                                            ))}
                                        </div>
                                        <span className={styles['rider-pagination-hint']}>← 스와이프하여 이동 →</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // 주간 현황 섹션 렌더링
    const renderWeeklyScoreSection = () => (
        <div ref={weeklyScoreRef} className={styles['section']} style={{ marginBottom: '50px' }}>
            <h2 className={styles['section-title']}>주간 현황</h2>
            <div className={styles['section-content']}>
                {/* 주간 현황 헤더 */}
                <div className={styles['weekly-score-header']}>
                    <h3 className={styles['weekly-score-title']}>주간 통계</h3>
                </div>

                {/* 주차 선택 */}
                <div className={styles['week-selector']}>
                    <CustomSelect
                        options={weeklySelectOptions}
                        value={selectedWeeklyOption.value}
                        onChange={handleWeeklyOptionChange}
                        placeholder="주차를 선택하세요"
                    />
                </div>

                {/* 주간 통계 - 총 수행 건수와 슬롯 달성률, 수락률 표시 */}
                <div className={styles['weekly-stats-list']}>
                    <div className={styles['weekly-stat-row']}>
                        <span className={styles['stat-label']}>총 수행 건수</span>
                        <span className={styles['stat-value']}>{weeklyStats.completedCount}건</span>
                    </div>
                    <div className={styles['weekly-stat-row']}>
                        <span className={styles['stat-label']}>슬롯 달성률</span>
                        <span className={styles['stat-value']}>
                            {adjustedSlotStats.achievementRate}%{' '}
                            <span className={styles['stat-sub']}>({adjustedSlotStats.achievedSlots} / {adjustedSlotStats.totalSlots}개)</span>
                        </span>
                    </div>
                    <div className={styles['weekly-stat-row']}>
                        <span className={styles['stat-label']}>주간 수락률</span>
                        <span className={styles['stat-value']}>
                            {weeklyStats.acceptanceRate !== null && !isNaN(weeklyStats.acceptanceRate)
                                ? `${weeklyStats.acceptanceRate}%`
                                : '-'}
                        </span>
                    </div>
                </div>

                {/* 요일별 테이블 - 시간대별 정보 포함 */}
                <div className={styles['weekly-table-container']}>
                    <table className={styles['weekly-table']}>
                        <thead>
                            <tr>
                                <th className={styles['weekly-th-date']}>날짜</th>
                                <th>아침점심피크</th>
                                <th>오후논피크</th>
                                <th>저녁피크</th>
                                <th>야간</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dailyData.map((day) => (
                                <tr
                                    key={day.day}
                                    className={`${styles['weekly-table-row']} ${day.day === todayDayOfWeek ? styles['today-row'] : ''}`}
                                >
                                    <td className={styles['weekly-td-date']}>
                                        {day.deliveryDate ? (
                                            <>
                                                <span className={styles['date-num']}>
                                                    {day.deliveryDate.slice(4, 6)}/{day.deliveryDate.slice(6, 8)}
                                                </span>
                                                <span className={styles['date-day']}>({day.label})</span>
                                            </>
                                        ) : (
                                            <span className={styles['date-day']}>{day.label}</span>
                                        )}
                                    </td>
                                    <td className={`${styles['weekly-td-slot']} ${day.hasData ? (day.morningPeak.isAchieved ? styles['achieved'] : styles['not-achieved']) : styles['no-data']}`}>
                                        {day.hasData ? (
                                            <>
                                                <span className={styles['slot-rate']}>{day.morningPeak.rate}%</span>
                                                <span className={styles['slot-status']}>{day.morningPeak.isAchieved ? '달성' : '미달성'}</span>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td className={`${styles['weekly-td-slot']} ${day.hasData ? (day.afternoonNonPeak.isAchieved ? styles['achieved'] : styles['not-achieved']) : styles['no-data']}`}>
                                        {day.hasData ? (
                                            <>
                                                <span className={styles['slot-rate']}>{day.afternoonNonPeak.rate}%</span>
                                                <span className={styles['slot-status']}>{day.afternoonNonPeak.isAchieved ? '달성' : '미달성'}</span>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td className={`${styles['weekly-td-slot']} ${day.hasData ? (day.eveningPeak.isAchieved ? styles['achieved'] : styles['not-achieved']) : styles['no-data']}`}>
                                        {day.hasData ? (
                                            <>
                                                <span className={styles['slot-rate']}>{day.eveningPeak.rate}%</span>
                                                <span className={styles['slot-status']}>{day.eveningPeak.isAchieved ? '달성' : '미달성'}</span>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td className={`${styles['weekly-td-slot']} ${day.hasData ? (day.night.isAchieved ? styles['achieved'] : styles['not-achieved']) : styles['no-data']}`}>
                                        {day.hasData ? (
                                            <>
                                                <span className={styles['slot-rate']}>{day.night.rate}%</span>
                                                <span className={styles['slot-status']}>{day.night.isAchieved ? '달성' : '미달성'}</span>
                                            </>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className={styles['data-notice']}>
                    ※ 데이터는 10~30분 간격으로 자동 갱신됩니다.
                </div>

                {/* 주간 라이더 현황 */}
                <div className={styles['weekly-rider-section']}>
                    <h3 className={styles['subsection-title']}>주간 라이더 현황</h3>
                    <div className={styles['rider-stats-cards']}>
                        <div className={`${styles['rider-stat-card']} ${styles['total']}`}>
                            <div className={styles['rider-stat-label']}>총 라이더</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.totalRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['active']}`}>
                            <div className={styles['rider-stat-label']}>운행중</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.activeRiders}명</div>
                        </div>
                        <div className={`${styles['rider-stat-card']} ${styles['completed']}`}>
                            <div className={styles['rider-stat-label']}>운행종료</div>
                            <div className={styles['rider-stat-value']}>{weeklyRiderStats.completedRiders}명</div>
                        </div>
                    </div>

                    {/* 주간 라이더 테이블 */}
                    <div
                        ref={weeklyRiderTableRef}
                        className={styles['rider-table-container']}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleWeeklyTouchEnd}
                    >
                        {isLoading ? (
                            <div className={styles['rider-empty']}>
                                라이더 정보를 불러오는 중...
                            </div>
                        ) : weeklyRiders.length === 0 ? (
                            <div className={styles['rider-empty']}>
                                등록된 라이더가 없습니다.
                            </div>
                        ) : (
                            <>
                                <div className={styles['rider-table-scroll']}>
                                <table className={styles['rider-table']}>
                                    <thead>
                                        <tr>
                                            <th>이름</th>
                                            <th>운영상태</th>
                                            <th>전화번호</th>
                                            <th>수행 건수</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedWeeklyRiders.map((rider) => (
                                            <tr
                                                key={rider.id}
                                                onClick={() => handleRiderRowClick(rider)}
                                                className={styles['rider-table-row']}
                                            >
                                                <td>
                                                    <span
                                                        className={styles['rider-name']}
                                                        onTouchStart={(e) => handleNameTouchStart(e, rider.name)}
                                                        onTouchEnd={handleNameTouchEnd}
                                                        onTouchCancel={handleNameTouchEnd}
                                                    >
                                                        {rider.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles['rider-status-badge']} ${styles[rider.status]}`}>
                                                        {rider.status === 'active' ? '운행중' : '운행종료'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a
                                                        href={`tel:${rider.phoneNumber}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className={styles['phone-link']}
                                                    >
                                                        {rider.phoneNumber}
                                                    </a>
                                                </td>
                                                <td>{rider.completedCount}건</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                                {/* 페이지 인디케이터 */}
                                {totalWeeklyRiderPages > 1 && (
                                    <div className={styles['rider-pagination']}>
                                        <span className={styles['rider-pagination-text']}>
                                            {weeklyRiderPage + 1} / {totalWeeklyRiderPages}
                                        </span>
                                        <div className={styles['rider-pagination-dots']}>
                                            {Array.from({ length: totalWeeklyRiderPages }, (_, i) => (
                                                <span
                                                    key={i}
                                                    className={`${styles['rider-pagination-dot']} ${i === weeklyRiderPage ? styles['active'] : ''}`}
                                                />
                                            ))}
                                        </div>
                                        <span className={styles['rider-pagination-hint']}>← 스와이프하여 이동 →</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // 라이더 상세 모달 렌더링
    const renderRiderModal = () => {
        if (!isRiderModalOpen || !selectedRider) return null;

        return (
            <div className={styles['rider-modal-overlay']} onClick={closeRiderModal}>
                <div className={styles['rider-modal']} onClick={(e) => e.stopPropagation()}>
                    <div className={styles['rider-modal-header']}>
                        <h3 className={styles['rider-modal-title']}>라이더 상세 정보</h3>
                        <button className={styles['rider-modal-close']} onClick={closeRiderModal}>
                            ×
                        </button>
                    </div>
                    <div className={styles['rider-modal-content']}>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>이름</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.name}</span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>운영상태</span>
                            <span className={`${styles['rider-modal-value']} ${styles[selectedRider.status]}`}>
                                {selectedRider.status === 'active' ? '운행중' : '운행종료'}
                            </span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>전화번호</span>
                            <a
                                href={`tel:${selectedRider.phoneNumber}`}
                                className={`${styles['rider-modal-value']} ${styles['phone-link']}`}
                            >
                                {selectedRider.phoneNumber}
                            </a>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>수행 건수</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.completedCount}건</span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>수락률</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.acceptanceRate}%</span>
                        </div>
                        <div className={styles['rider-modal-divider']} />
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>아침/점심 피크</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.morningPeakCount}건</span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>오후 논피크</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.afternoonNonPeakCount}건</span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>저녁 피크</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.eveningPeakCount}건</span>
                        </div>
                        <div className={styles['rider-modal-row']}>
                            <span className={styles['rider-modal-label']}>야간</span>
                            <span className={styles['rider-modal-value']}>{selectedRider.nightCount}건</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // 자동 갱신 라벨 가져오기
    const getAutoRefreshLabel = () => {
        const option = autoRefreshOptions.find(opt => opt.value === autoRefreshInterval);
        return option ? option.label : '사용안함';
    };

    const goToMyPage = () => {
        navigate('/my-page');
    };

    // 점검 화면 확인 처리
    const handleMaintenanceAcknowledge = () => {
        if (activeMaintenance) {
            sessionStorage.setItem('maintenance_acknowledged', 'true');
            sessionStorage.setItem('maintenance_acknowledged_seqno', String(activeMaintenance.seqNo));
            setMaintenanceAcknowledged(true);
        }
    };

    // 디버깅: 점검 상태 로그
    useEffect(() => {
        console.log('HomePageForSupplier - isMaintenance:', isMaintenance, 'activeMaintenance:', activeMaintenance, 'maintenanceAcknowledged:', maintenanceAcknowledged, 'maintenanceLoading:', maintenanceLoading);
    }, [isMaintenance, activeMaintenance, maintenanceAcknowledged, maintenanceLoading]);

    // 점검 데이터 로딩 중이면 대기
    if (maintenanceLoading) {
        return null; // 또는 로딩 스피너
    }

    // 점검 중이고 아직 확인하지 않았으면 MaintenanceScreen 전체 화면 표시
    if (isMaintenance && activeMaintenance && !maintenanceAcknowledged) {
        return (
            <MaintenanceScreen 
                maintenance={activeMaintenance} 
                showDashboardButton={true}
                onDashboardClick={handleMaintenanceAcknowledge}
            />
        );
    }

    return (
        <div className={`screen active ${styles['supplier-home-screen']}`}>
            {/* 헤더 */}
            <div className={styles['supplier-header']}>
                <div className={styles['supplier-header-top']}>
                    <div className={styles['supplier-title-section']}>
                        <h1 className={styles['supplier-title']}>Vpay</h1>
                        <div className={styles['supplier-info']}>
                            <span className={styles['supplier-name']}>{supplierInfo.supplierName}</span>
                            <span className={styles['supplier-update']}>업데이트 - {lastUpdate}</span>
                        </div>
                    </div>
                    <div className={styles['supplier-header-actions']}>
                        {/*<div className={styles['auto-refresh-wrapper']}>*/}
                        {/*    <button*/}
                        {/*        className={`${styles['auto-refresh-btn']} ${autoRefreshInterval !== 'off' ? styles['active'] : ''}`}*/}
                        {/*        onClick={handleAutoRefreshToggle}*/}
                        {/*    >*/}
                        {/*        자동갱신 ({getAutoRefreshLabel()})*/}
                        {/*    </button>*/}
                        {/*    {showAutoRefreshSelect && (*/}
                        {/*        <div className={styles['auto-refresh-dropdown']}>*/}
                        {/*            {autoRefreshOptions.map((option) => (*/}
                        {/*                <button*/}
                        {/*                    key={option.value}*/}
                        {/*                    className={`${styles['auto-refresh-option']} ${autoRefreshInterval === option.value ? styles['selected'] : ''}`}*/}
                        {/*                    onClick={() => handleAutoRefreshChange(option.value as AutoRefreshInterval)}*/}
                        {/*                >*/}
                        {/*                    {option.label}*/}
                        {/*                </button>*/}
                        {/*            ))}*/}
                        {/*        </div>*/}
                        {/*    )}*/}
                        {/*</div>*/}
                        <BalanceDisplay />
                        <button className={styles['settings-btn']} onClick={goToMyPage}>
                            <i className="fas fa-cog"></i>
                        </button>
                    </div>
                </div>
                
                {renderTabs()}
            </div>

            {/* 메인 컨텐츠 - 모든 섹션 표시 */}
            <div ref={containerRef} className={styles['supplier-container']}>
                {renderSettingsSection()}
                {renderDailyStatusSection()}
                {renderWeeklyScoreSection()}
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="delivery" />

            {/* 라이더 상세 모달 */}
            {renderRiderModal()}

            {/* 이름 툴팁 (Long Press) */}
            {nameTooltip.visible && (
                <div
                    className={styles['name-tooltip']}
                    style={{
                        left: nameTooltip.x,
                        top: nameTooltip.y,
                    }}
                    onClick={hideNameTooltip}
                    onTouchEnd={hideNameTooltip}
                >
                    {nameTooltip.name}
                </div>
            )}

            {/* 공지사항 팝업 */}
            {popupNotices.length > 0 && popupNotices[currentNoticeIndex] && (
                <NoticePopupModal
                    notice={popupNotices[currentNoticeIndex]}
                    zIndex={1000 + (popupNotices.length - currentNoticeIndex)}
                    onClose={handleCloseNotice}
                    onHideForToday={handleHideForToday}
                />
            )}
        </div>
    );
};

export default HomePageForSupplier;
